<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>

            <div class="modal-content">
                <div class="popup-dialog popup-dialog--wide">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ $t('account.security.totp.setup.title') }}</h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body popup-body-bottom-border">
                        <div class="totp-setup-columns">
                            <div class="totp-setup-qr">
                                <div class="qr-code-container" v-if="this.setupData && this.setupData.qr_code_svg">
                                    <img :src="this.setupData.qr_code_svg" alt="QR Code" class="qr-code-image"/>
                                </div>

                                <a
                                    href="#"
                                    class="totp-copy-button"
                                    @click.prevent="copySecret"
                                >
                                    {{
                                        copied ? $t('account.security.totp.setup.copied') : $t('account.security.totp.setup.copy_code')
                                    }}
                                </a>
                            </div>

                            <div>
                                <strong>{{ $t('account.security.totp.setup.step_1_title') }}</strong>
                                <p>{{ $t('account.security.totp.setup.step_1_description') }}</p><br/>

                                <strong>{{ $t('account.security.totp.setup.step_2_title') }}</strong>
                                <p>{{ $t('account.security.totp.setup.step_2_description') }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="popup-body popup-security-input">
                        <security-visual ref="securityVisual"/>

                        <div class="code-input-container"
                             :class="{'is-hidden': isUnlocking || isUnlocked}">
                            <code-input
                                ref="codeInput"
                                v-model="code"
                                :length="6"
                                type="numeric"
                                :disabled="isConfirming"
                                :auto-submit="true"
                                @submit="confirmSetup"
                            />
                        </div>

                        <p v-if="errorMessage && !isUnlocking && !isUnlocked"
                           class="totp-error">{{ errorMessage }}</p>
                    </div>

                    <div class="popup-footer">
                        <a @click.prevent="destroy" href="#" role="button"
                           v-if="!isUnlocking && !isUnlocked">
                            {{ $t('account.security.totp.setup.cancel') }}
                        </a>
                        <button
                            class="button is-rounded is-success"
                            :class="{'is-loading': isConfirming || isUnlocking || isUnlocked}"
                            :disabled="!isCodeValid || isConfirming || isUnlocking || isUnlocked"
                            @click="confirmSetup"
                        >
                            {{ $t('account.security.totp.setup.activate') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import CodeInput from '../../../auth/CodeInput.vue';
import SecurityVisual from '../../../auth/SecurityVisual.vue';
import SecurityAnimationMixin from '../../../auth/SecurityAnimationMixin.js';

export default {
    components: {CodeInput, SecurityVisual},
    mixins: [SecurityAnimationMixin],

    props: {
        setupData: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            // isUnlocking + isUnlocked come from SecurityAnimationMixin
            isConfirming: false,
            errorMessage: null,
            code: '',
            copied: false,
        };
    },

    computed: {
        isCodeValid() {
            return this.code.length === 6 && /^\d{6}$/.test(this.code);
        },
    },

    methods: {
        async confirmSetup() {
            if (this.isCodeValid === false || this.isConfirming) {
                return;
            }

            this.isConfirming = true;
            this.errorMessage = null;

            try {
                const response = await axios.post('/api/user/totp-devices/confirm', {
                    code: this.code,
                });

                // Show success animations (shared via SecurityAnimationMixin)
                this.triggerUnlock();

                // Give user time to see the success animation before closing
                setTimeout(() => {
                    this.$emit('setup-complete');
                    this.$buefy.toast.open({
                        message: response.data.message,
                        type: 'is-success',
                    });
                    this.destroy();
                }, 1200);
            } catch (error) {
                this.errorMessage = error.response?.data?.message;

                // Show error animations (shared via SecurityAnimationMixin)
                this.triggerDeny();
                this.clearCodeInput();
                this.code = '';
            } finally {
                this.isConfirming = false;
            }
        },

        async copySecret() {
            try {
                await navigator.clipboard.writeText(this.setupData.secret);
                this.copied = true;
                setTimeout(() => {
                    this.copied = false;
                }, 2000);
            } catch {
                // Fallback for older browsers
                const textArea = document.createElement('textarea');
                textArea.value = this.setupData.secret;
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                this.copied = true;
                setTimeout(() => {
                    this.copied = false;
                }, 2000);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.popup-dialog--wide {
    max-width: 640px;
}

.totp-setup-columns {
    display: flex;
    align-items: flex-start;
    gap: 2rem;

    @media screen and (max-width: 768px) {
        flex-direction: column;
        align-items: center;
    }
}

.totp-setup-qr {
    display: flex;
    padding: 0.5rem;
    flex-direction: column;
    align-items: center;
    border: 1px solid #F6F6F6;
    background: #FFF;

    .qr-code-container {
        width: 175px;
        height: 175px;
        margin-bottom: 0.3rem;

        .qr-code-image {
            width: 100%;
            height: 100%;
        }
    }

    .totp-copy-button {
        color: #6A7272;
        font-size: 0.75rem;
        line-height: normal;
        text-decoration-line: underline;
    }
}

.totp-error {
    color: #DE134E;
    font-size: 0.875rem;
    text-align: center;
}
</style>
